import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'
import { subWeeks } from 'date-fns/subWeeks'
import { subMonths } from 'date-fns/subMonths'
import { subHours } from 'date-fns'

import type { DatePeriod, PendingDatePeriod, DatePeriodKey, DateRange } from 'src/Types/DatePeriod'

export const predefinedDatePeriodKeys: DatePeriodKey[] = [
  'lastHour',
  'today',
  'last24',
  'lastDays',
  'lastWeek',
  'lastMonth',
  'selectTimePeriod',
]

/**
 * Get DateRange for a given key:
 *
 * **lastHour** ~ last 1 hour ~~ Siste time: [now - 1 hour, now]
 *
 * **today** ~ today ~~ I dag: [startOfToday().valueOf(), now]
 *
 * **last24** ~ last 24 hours ~~ Siste 24 timer: [now - 24 hours, now]
 *
 * **lastDays** ~ last 3 days ~~ Siste tre dagene:
 *  - Now: [now - 3 days, now] ~ last 3 days = 72 hours, overlaps 4 calendar days
 *  - Before: [startOfToday().valueOf() - 2 days, now] ~ last [2,3) days = [48,72) hours, overlaps 3 calendar days
 *
 * **lastWeek** ~ last 7 days ~~ Siste uken:
 *  - Now: [now - 7 days, now] ~ last 7 days, overlaps 8 calendar days
 *  - Before: [now - 7 days, now] ~ last 7 days, overlaps 8 calendar days
 *
 * **lastMonth** ~ last 672-744 hours (prev. month's length) ~~ Siste måneden:
 *  - Now: [now - 1 month, now] ~ last 672-744 hours
 *  - Before: [startOfToday().valueOf() - 1 month, now] ~ last 672-768 hours
 *
 * @param key DatePeriodKey
 * @returns DateRange
 */
export const getDateRangeForKey = (key: DatePeriodKey): DateRange => {
  const now = Date.now()
  let start: number
  switch (key) {
    // [now - 1 hour, now]
    case 'lastHour':
      start = subHours(now, 1).valueOf()
      break

    // [now - 24 hours, now] ~ last 24h, overlaps 2 calendar days
    case 'last24':
      start = subDays(now, 1).valueOf()
      break

    // Before:  [startOfToday().valueOf() - 2 days,  now] ~ last [48,72)h, overlaps 3 calendar days
    // Now:     [now  - 2 days,  now] ~ last 72h, overlaps 4 calendar days
    case 'lastDays':
      start = subDays(now, 3).valueOf()
      break

    // Before: [startOfToday().valueOf() - 7 days, now]   ~ last [7,8)x 24h, overlaps 8 calendar days
    // Now:    [now  - 7 days, now]   ~ last 7x 24h, overlaps 8 calendar days
    case 'lastWeek':
      start = subWeeks(now, 1).valueOf()
      break

    // Before: [startOfToday().valueOf() - 1 month, now]  ~ last [672,768)h, overlaps 29-32 cal. days
    // Now:    [now  - 30 days, now]  ~ last 30x 24h
    case 'lastMonth':
      start = subDays(now, 30).valueOf()
      break

    // [startOfToday, now] ~ overlaps 1-24 hours on the clock
    case 'today':
    default:
      start = startOfToday().valueOf()
  }
  return { fromDate: start, toDate: now }
}

const periodIsKey = (datePeriod: DatePeriod | PendingDatePeriod): datePeriod is DatePeriodKey => typeof datePeriod === 'string'

export const getDateRange = (datePeriod: DatePeriod) => (periodIsKey(datePeriod) ? getDateRangeForKey(datePeriod) : datePeriod)

export const getPendingDateRange = (datePeriod: PendingDatePeriod) => {
  if (!periodIsKey(datePeriod)) {
    const { fromDate, toDate } = datePeriod
    const isValidRange = fromDate && toDate && fromDate < toDate

    return isValidRange ? getDateRange(datePeriod as DatePeriod) : undefined
  }

  return getDateRange(datePeriod)
}

export const getDatePeriodKey = (datePeriod: DatePeriod | PendingDatePeriod): DatePeriodKey | undefined =>
  periodIsKey(datePeriod) ? datePeriod : 'selectTimePeriod'
